(() => {
  const { CustomHTMLElement, DrawerContent, triggerEvent } = window.ThemeCore || {};
  if (!CustomHTMLElement || !DrawerContent || !triggerEvent) {
    return;
  }

  const defineCE = (name, ctor, options) => {
    if (!window.customElements.get(name)) {
      window.customElements.define(name, ctor, options);
    }
  };

  const throttle = (callback, delay = 100) => {
    let last = 0;
    let timer = null;
    return (...args) => {
      const now = Date.now();
      const remaining = delay - (now - last);
      if (remaining <= 0) {
        last = now;
        callback(...args);
        return;
      }
      if (timer) return;
      timer = setTimeout(() => {
        timer = null;
        last = Date.now();
        callback(...args);
      }, remaining);
    };
  };

  // Desktop mega menu / dropdowns (minimal, no animation framework)
  class DesktopNavigation extends CustomHTMLElement {
    connectedCallback() {
      this.currentItem = null;

      this.delegate.on(
        "mouseenter",
        ".has-dropdown",
        (event, target) => {
          if (event.target === target && event.relatedTarget !== null) {
            this.openDropdown(target);
          }
        },
        true
      );

      this.delegate.on(
        "click",
        ".header__linklist-link[aria-expanded], .nav-dropdown__link[aria-expanded]",
        (event, target) => {
          if (target.getAttribute("aria-expanded") === "true") return;
          event.preventDefault();
          this.openDropdown(target.parentElement);
        }
      );

      this.delegate.on("shopify:block:select", (event) =>
        this.openDropdown(event.target.parentElement)
      );
      this.delegate.on("shopify:block:deselect", (event) =>
        this.closeDropdown(event.target.parentElement)
      );
    }

    openDropdown(parentElement) {
      const menuItem = parentElement.querySelector("[aria-controls]");
      if (!menuItem) return;

      const dropdown = parentElement.querySelector(
        `#${menuItem.getAttribute("aria-controls")}`
      );
      if (!dropdown) return;

      menuItem.setAttribute("aria-expanded", "true");
      dropdown.removeAttribute("hidden");
      this.currentItem = parentElement;

      const onLeave = (event) => {
        if (event.relatedTarget !== null) {
          this.closeDropdown(parentElement);
          parentElement.removeEventListener("mouseleave", onLeave);
        }
      };
      parentElement.addEventListener("mouseleave", onLeave);

      this.dispatchEvent(
        new CustomEvent("desktop-nav:dropdown:open", { bubbles: true })
      );
    }

    closeDropdown(parentElement) {
      const menuItem = parentElement.querySelector("[aria-controls]");
      if (!menuItem) return;

      const dropdown = parentElement.querySelector(
        `#${menuItem.getAttribute("aria-controls")}`
      );
      if (!dropdown) return;

      menuItem.setAttribute("aria-expanded", "false");
      dropdown.setAttribute("hidden", "");
      this.dispatchEvent(
        new CustomEvent("desktop-nav:dropdown:close", { bubbles: true })
      );
    }
  }
  defineCE("desktop-navigation", DesktopNavigation);

  // Mobile navigation drawer
  class MobileNavigation extends DrawerContent {
    attributeChangedCallback(name, oldValue, newValue) {
      super.attributeChangedCallback(name, oldValue, newValue);
      if (name === "open") {
        triggerEvent(this, this.open ? "mobile-nav:open" : "mobile-nav:close");
      }
    }
  }
  defineCE("mobile-navigation", MobileNavigation);

  // Store header sticky/transparent logic
  class StoreHeader extends CustomHTMLElement {
    connectedCallback() {
      if (window.ResizeObserver) {
        this.resizeObserver = new ResizeObserver(
          this._updateCustomProperties.bind(this)
        );
        this.resizeObserver.observe(this);
        const wrapper = this.querySelector(".header__wrapper");
        if (wrapper) this.resizeObserver.observe(wrapper);
      }

      if (this.isTransparent) {
        this.isTransparencyDetectionLocked = false;

        this.delegate.on(
          "desktop-nav:dropdown:open",
          () => (this.lockTransparency = true)
        );
        this.delegate.on(
          "desktop-nav:dropdown:close",
          () => (this.lockTransparency = false)
        );
        this.rootDelegate.on(
          "mobile-nav:open",
          () => (this.lockTransparency = true)
        );
        this.rootDelegate.on(
          "mobile-nav:close",
          () => (this.lockTransparency = false)
        );

        this.delegate.on("mouseenter", this._checkTransparentHeader.bind(this), true);
        this.delegate.on("mouseleave", this._checkTransparentHeader.bind(this));

        if (this.isSticky) {
          this._checkTransparentHeader();
          this._onWindowScrollListener = throttle(
            this._checkTransparentHeader.bind(this),
            100
          );
          window.addEventListener("scroll", this._onWindowScrollListener);
        }
      }
    }

    disconnectedCallback() {
      super.disconnectedCallback();
      if (this.resizeObserver) this.resizeObserver.disconnect();
      if (this.isTransparent && this.isSticky && this._onWindowScrollListener) {
        window.removeEventListener("scroll", this._onWindowScrollListener);
      }
    }

    get isSticky() {
      return this.hasAttribute("sticky");
    }
    get isTransparent() {
      return this.hasAttribute("transparent");
    }
    get transparentHeaderThreshold() {
      return 25;
    }
    set lockTransparency(value) {
      this.isTransparencyDetectionLocked = value;
      this._checkTransparentHeader();
    }

    _updateCustomProperties(entries) {
      entries.forEach((entry) => {
        if (entry.target === this) {
          const height = entry.target.clientHeight;
          document.documentElement.style.setProperty("--header-height", `${height}px`);
        }
        if (entry.target.classList.contains("header__wrapper")) {
          const heightWithoutNav = entry.target.clientHeight;
          document.documentElement.style.setProperty(
            "--header-height-without-bottom-nav",
            `${heightWithoutNav}px`
          );
        }
      });
    }

    _checkTransparentHeader(event) {
      if (
        this.isTransparencyDetectionLocked ||
        window.scrollY > this.transparentHeaderThreshold ||
        (event && event.type === "mouseenter")
      ) {
        this.classList.remove("header--transparent");
      } else {
        this.classList.add("header--transparent");
      }
    }
  }
  defineCE("store-header", StoreHeader);
})();

